import { useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { terraProxyApiClient } from '../../clients/testerClient';
import type { EndpointReturnDto } from '@terra/shared-contracts-endpoints';

const EMPTY_ENDPOINTS: EndpointReturnDto[] = [];

export const ENDPOINTS_QUERY_KEY = 'endpoints';

export const useInvalidateEndpoints = () => {
  const queryClient = useQueryClient();
  return useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: [ENDPOINTS_QUERY_KEY],
      exact: false,
    });
  }, [queryClient]);
};

export const useEndpoints = ({
  applicationId,
  enabled = true,
}: {
  applicationId: string | null | undefined;
  enabled?: boolean;
}) => {
  const queryClient = useQueryClient();

  const result = useQuery({
    queryKey: [ENDPOINTS_QUERY_KEY, applicationId],
    queryFn: async () => {
      if (!applicationId) {
        return { endpoints: [] };
      }
      return terraProxyApiClient.getEndpointsByApplication(applicationId);
    },
    enabled: !!applicationId && enabled,
    staleTime: 30000,
  });

  const invalidate = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: [ENDPOINTS_QUERY_KEY],
      exact: false,
    });
  }, [queryClient]);

  const endpoints = useMemo(
    () => result.data?.endpoints ?? EMPTY_ENDPOINTS,
    [result.data?.endpoints],
  );

  return {
    endpoints,
    isLoading: result.isLoading,
    error: result.error,
    refetch: result.refetch,
    isFetching: result.isFetching,
    invalidate,
  };
};
